<template>
    <div class="flex flex-col gap-10">
        <div class="flex flex-col gap-2">
            <h1 class="text-xl font-semibold primary_color truncate">Confirm account details</h1>
            <p class="text-sm font-normal leading-[17px] secondary_color line-clamp-1">
                Confirm the Whatsapp Business Account details.    
            </p>
        </div>

        <div class="flex flex-col gap-4">
            <!-- Vendor -->
            <div class="flex flex-col gap-1">
                <span class="text-[13px] font-normal leading-[13px] secondary_color">Vendor</span>
                <span class="text-sm font-normal leading-4 primary_color">{{ selectedVendor?.toUpperCase() || 'MSG91' }}</span>
            </div>

            <!-- Business Numbers -->
            <div class="flex flex-col gap-1">
                <span class="text-[13px] font-normal leading-[13px] secondary_color">Business Numbers</span>
                <div class="flex flex-row gap-2 mt-1 flex-wrap">
                    <div
                        v-for="(numberObj, index) in phoneNumbersList"
                        :key="index"
                        :class="[
                            'flex items-center gap-1.5 py-0.5 px-2 rounded-2xl w-fit transition-all',
                            isPhoneNumberSelected(numberObj.integrated_number) 
                                ? 'border_8 bg_8 selected-number' 
                                : 'border_8 bg_8 hover:bg_6',
                            phoneNumbersList.length > 1 ? 'cursor-pointer' : ''
                        ]"
                        @click="phoneNumbersList.length > 1 ? togglePhoneNumber(numberObj.integrated_number) : null"
                    >
                        <span class="text-sm font-normal leading-4 primary_color">{{ numberObj.integrated_number }}</span>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, ref, onMounted, nextTick, watch } from "vue";

const props = defineProps({
    selectedVendor: {
        type: String,
        default: null,
    },
    vendorData: {
        type: Object,
        default: () => ({}),
    },
});

const emit = defineEmits(["update:vendorData"]);

const phoneNumbersList = computed(() => {
    const phoneNumbers = props.vendorData?.phoneNumbers || [];
    if (Array.isArray(phoneNumbers)) {
        return phoneNumbers
            .map((number) => {
                if (typeof number === "string") {
                    return { integrated_number: number };
                }
                return {
                    integrated_number: number.integrated_number || number.phone_number || number.phone || "",
                    ...number,
                };
            })
            .filter((number) => number.integrated_number !== "");
    }
    return [];
});

const selectedPhoneNumbers = ref(new Set());
const hasInitialized = ref(false);

const updateVendorData = () => {
    emit("update:vendorData", {
        ...props.vendorData,
        selectedPhoneNumbers: Array.from(selectedPhoneNumbers.value),
    });
};

const initializeSelection = () => {
    if (hasInitialized.value) return;
    
    const list = phoneNumbersList.value;
    if (list.length === 0) return;
    
    const allNumbers = list
        .map(numberObj => numberObj.integrated_number)
        .filter(num => num !== "");
    
    if (allNumbers.length > 0) {
        selectedPhoneNumbers.value = new Set(allNumbers);
        hasInitialized.value = true;
        nextTick(() => {
            updateVendorData();
        });
    }
};

onMounted(() => {
    initializeSelection();
});

watch(
    () => phoneNumbersList.value.length,
    (newLength) => {
        if (newLength > 0 && !hasInitialized.value) {
            initializeSelection();
        }
    },
    { immediate: true }
);

const isPhoneNumberSelected = (integratedNumber) => {
    return selectedPhoneNumbers.value.has(integratedNumber);
};

const togglePhoneNumber = (integratedNumber) => {
    if (phoneNumbersList.value.length === 1) {
        return;
    }
    
    if (selectedPhoneNumbers.value.has(integratedNumber)) {
        if (selectedPhoneNumbers.value.size > 1) {
            selectedPhoneNumbers.value.delete(integratedNumber);
            updateVendorData();
        }
    } else {
        selectedPhoneNumbers.value.add(integratedNumber);
        updateVendorData();
    }
};

const validateBeforeNext = () => {
    if (selectedPhoneNumbers.value.size === 0) {
        return false;
    }
    return true;
};

defineExpose({
    validateBeforeNext,
});
</script>

<style scoped>
.selected-number {
    background-color: var(--white-opacity-14) !important;
    border-color: var(--white-opacity-14) !important;
}
</style>

